import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Company } from '../../shared/models/company';
import { ICompaniesPathParams, ICompaniesQueryParams, ICreateCompany, IUpdateCompany } from '../../shared/models/company.model';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly baseUrl = environment.baseBackendUrl;

  selectedCompany$: BehaviorSubject<Company | undefined> = new BehaviorSubject<Company | undefined>(undefined);

  get selectedCompany(): Observable<Company | undefined> {
    return this.selectedCompany$ as Observable<Company | undefined>;
  }

  set selectedCompany(value: Company | undefined) {
    this.selectedCompany$.next(value);
    if (value) {
      localStorage.setItem('selectedCompany', JSON.stringify(value))
    } else {
      localStorage.removeItem('selectedCompany');
    }
  }

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) {
    if (localStorage.getItem('selectedCompany')) {
      this.selectedCompany = JSON.parse(localStorage.getItem('selectedCompany')!)
    }
  }

  /**
   * Obtiene todas las companies con filtros opcionales
   * @param params Parámetros de consulta OData
   */
  getCompanies(params?: ICompaniesQueryParams): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Companies`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene una company por ID
   * @param key ID de la company
   * @param params Parámetros de consulta opcionales
   */
  getCompanyById(key: number, params?: { $select?: string }): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Companies(${key})`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene el conteo de companies
   * @param params Parámetros de filtro opcionales
   */
  getCompaniesCount(params?: { $filter?: string }): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/odata/Companies/$count`, {
      params: this.buildQueryParams(params)
    }).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Crea una nueva company
   * @param companyData Datos de la nueva company
   */
  createCompany(companyData: ICreateCompany): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/odata/Companies`, companyData).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una company por ID
   * @param key ID de la company a eliminar
   */
  deleteCompany(key: number): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/odata/Companies(${key})`).pipe(
      catchError((error) => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Construye los parámetros de consulta HTTP
   * @param params Parámetros de consulta
   */
  private buildQueryParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null) {
          httpParams = httpParams.set(key, params[key].toString());
        }
      });
    }
    return httpParams;
  }
}
